import React, { useEffect, useState, createRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import {
  DialogActions,
  DialogContent,
  Typography,
  TextField,
  InputAdornment,
  Box,
} from '@mui/material';
import { AddRounded, FilterAltOutlined, Search } from '@mui/icons-material';

import api from '@src/apis';
import actions from '@src/redux/actions';
import {
  OLD_VERSION_DUBBING_VOICE_CODES,
  VOICE_LEVEL,
  VOICE_PAGINATION_LIMIT,
} from '@src/constants/voice';
import { MOBILE_BREAKPOINT } from '@src/constants';
import { PLAYING_AUDIO_TYPE, SILENCE_AUDIO_URL } from '@src/constants/tts';
import debounce from '@src/utils/debounce';
import Dialog from '@src/components/Dialog';
import ProcessHandler from '@src/components/ProcessHandler';
import CustomPagination from '@src/components/Pagination';
import FirstVoiceBoxImg from '@src/assets/images/create-first-voice-box.svg';
import { useHistory } from 'react-router-dom';

import route from '@src/constants/route';
import { useCheckDubbingRoute } from '@src/hooks/useCheckDubbingRoute';
import CommonButton from '@src/new-components/Button';
import VoiceItem from './VoiceItem';
import VoicesSort from './VoicesSort';
import VoicesFilter from './VoicesFilter';
import MobileVoicesFilter from './MobileVoicesFilter';
import { StyledVoices } from './index.style';

const initFilterOpens = {
  gender: true,
  features: true,
  level: true,
  languageCode: true,
  voiceOwnership: true,
};

const VoicesDialog = ({
  open,
  activeVoiceId,
  activeVoiceCode,
  defaultFilter = {},
  hasLanguageFilter,
  usingPackageType,
  onClose,
  onChangeVoice,
  oldDubbingVoices,
  defaultVoiceOwnership,
  onDeleteCommunityVoice,
  voiceCode,
  onDeleteVoiceCode,
}) => {
  const isDubbingRoute = useCheckDubbingRoute();
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [voices, setVoices] = useState([]);
  const [openSort, setOpenSort] = useState(false);
  const [openFilter, setOpenFilter] = useState(
    window.innerWidth > MOBILE_BREAKPOINT,
  );
  const [loading, setLoading] = useState(false);
  const [audioLink, setAudioLink] = useState('');
  const [search, setSearch] = useState('');
  const [filterOpens, setFilterOpens] = useState(initFilterOpens);

  const [audioType, setAudioType] = useState(PLAYING_AUDIO_TYPE.SILENCE);
  const country = localStorage.getItem('country');
  const { filterVoice } = useSelector((state) => state.voice);
  const { ttsPreset } = useSelector((state) => state.synthesisRequest);
  const { user } = useSelector((state) => state.auth);
  const history = useHistory();
  const { t } = useTranslation();
  const audioRef = createRef(null);
  const dispatch = useDispatch();

  const handleChangeFilter = (value) => {
    dispatch(actions.voice.changeFilterVoice(value));
    setPage(1);
  };

  const handleChangeFilterOpens = (name) => {
    const newFilterOpen = { ...filterOpens, [name]: !filterOpens[name] };
    setFilterOpens(newFilterOpen);
  };

  const handleChangePage = (newPage) => setPage(newPage);
  const handleReset = () => {
    setSearch('');
    dispatch(actions.voice.resetFilterVoice());
  };

  const fetchVoices = async () => {
    setLoading(true);
    const hasMultilingualValue = filterVoice?.level?.find(
      (item) => item === VOICE_LEVEL.MULTILINGUAL,
    );
    const gender = filterVoice?.gender?.length
      ? filterVoice.gender.map((item) => item).join(',')
      : '';
    const languageCode =
      filterVoice?.languageCode?.length || hasMultilingualValue
        ? [...filterVoice.languageCode, hasMultilingualValue]
            .map((item) => item)
            .join(',')
        : '';
    const features = filterVoice?.features?.length
      ? filterVoice.features.map((item) => item).join(',')
      : '';
    const level = filterVoice?.level?.length
      ? filterVoice.level
          .flatMap((item) => (item === VOICE_LEVEL.MULTILINGUAL ? [] : item))
          .join(',')
      : '';
    const voiceOwnership = defaultVoiceOwnership || filterVoice?.voiceOwnership;
    let data = {};
    if (!isDubbingRoute) {
      data = await api.voices.getVoicesV2({
        search: search.trim(),
        gender,
        languageCode,
        level,
        features,
        active: true,
        offset: (page - 1) * VOICE_PAGINATION_LIMIT,
        limit: oldDubbingVoices ? undefined : VOICE_PAGINATION_LIMIT,
        voiceOwnership,
        code: voiceCode,
        ...defaultFilter,
      });
      if (onDeleteCommunityVoice) {
        onDeleteCommunityVoice();
      }
      if (onDeleteVoiceCode) onDeleteVoiceCode();
    } else
      data = await api.voices.getVoices({
        search: search.trim(),
        gender,
        languageCode: isDubbingRoute ? 'vi-VN' : languageCode,
        level,
        features,
        active: true,
        offset: (page - 1) * VOICE_PAGINATION_LIMIT,
        limit: oldDubbingVoices ? undefined : VOICE_PAGINATION_LIMIT,
        ...defaultFilter,
      });
    setLoading(false);
    if (data.result) {
      const { hasDubbing } = defaultFilter;
      if (hasDubbing && oldDubbingVoices) {
        const oldVoices = data.result.voices.filter((voice) =>
          OLD_VERSION_DUBBING_VOICE_CODES.includes(voice.code),
        );
        setVoices(oldVoices);
        setTotal(OLD_VERSION_DUBBING_VOICE_CODES.length);
      } else {
        setVoices(data.result.voices);
        setTotal(data.result.metadata.total);
      }
    }
  };

  const handleOpenFilter = (value) => {
    setOpenFilter(value);
    if (value) setOpenSort(false);
  };

  const handleCloseVoiceFilter = () => setOpenFilter(false);

  const handleAudioEnd = () => {
    if (audioType === PLAYING_AUDIO_TYPE.BACKGROUND_MUSIC) {
      setAudioType(PLAYING_AUDIO_TYPE.SILENCE);
      audioRef.current.currentTime = 0;
      audioRef.current.pause();
      const newVoices = voices.map((item) =>
        item.playStatus ? { ...item, playStatus: false } : item,
      );
      setVoices(newVoices);
      return;
    }

    if (audioLink) {
      setAudioType(PLAYING_AUDIO_TYPE.BACKGROUND_MUSIC);
      audioRef.current.src = audioLink;
      audioRef.current.load();
      audioRef.current.play();
      return;
    }

    audioRef.current.src = SILENCE_AUDIO_URL;
    audioRef.current.load();
    audioRef.current.play();
  };

  const handleClose = () => {
    setOpenFilter(window.innerWidth > MOBILE_BREAKPOINT);
    setOpenSort(false);
    setFilterOpens(initFilterOpens);
    setAudioLink('');
    handleAudioEnd();
    setSearch('');
    onClose();
  };

  const handleChangePlayAudio = (voice) => {
    dispatch(actions.audioPlayer.updateStatus(false));
    const audio = audioRef.current;
    if (!audioLink || voice.demo !== audioLink) {
      setAudioType(PLAYING_AUDIO_TYPE.SILENCE);
      audio.src = SILENCE_AUDIO_URL;
      audio.load();
      audio.play();
      setAudioLink(voice.demo);
      audio.load();
    }

    if (voice.playStatus) {
      audio.pause();
    } else {
      audio.play();
    }

    const newVoices = voices.map((item) =>
      item.id === voice.id
        ? { ...voice, playStatus: !voice.playStatus }
        : { ...item, playStatus: false },
    );
    setVoices(newVoices);
  };

  const handleSelectedVoice = (voice) => {
    if (ttsPreset && ttsPreset?.voice?.id !== voice.id) {
      dispatch(actions.synthesisRequest.updateCurrentTTSPreset(null));
    }
    onChangeVoice(voice);
    handleClose();
  };

  const handleChangeSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  useEffect(() => {
    if (open) fetchVoices();
  }, [open, page, filterVoice]);

  useEffect(() => {
    debounce(fetchVoices, 500)(search);
  }, [search]);

  useEffect(() => {
    dispatch(actions.voice.resetFilterVoice(user.packageCode));
  }, [country]);

  const handleRedirectToVoiceCloning = () => {
    history.push(route.VOICE_CLONING_VOICES);
  };

  return (
    <Dialog
      maxWidth="md"
      title={t('chooseVoice')}
      open={open}
      fullWidth
      dataAttribute={{ 'data-id': 'close-voice-list' }}
      onClose={handleClose}
    >
      <StyledVoices openSearch={openSort || openFilter}>
        <DialogActions classes={{ root: 'dialog-action' }}>
          <div className="button-wrapper">
            <div
              className={classNames('action-button', { active: openFilter })}
              onClick={() => handleOpenFilter(!openFilter)}
              role="presentation"
            >
              <Typography variant="body2" color="darkBlue">
                {t('filter')}
              </Typography>
              <FilterAltOutlined
                data-id={openFilter ? 'hide-voice-filter' : 'show-voice-filter'}
                className="action-icon"
              />
            </div>
            {/*  <div
            
            className={classNames('action-button', { active: openSort })}
            onClick={() => handleOpenSort(!openSort)}
            role="presentation"
          >
            <Typography variant="body2"> {t('sort')} </Typography>
            <Shuffle className="action-icon" />
          </div> */}
          </div>
          <Box display="flex" alignItems="center" gap="16px">
            <TextField
              size="small"
              variant="standard"
              className="search-voice"
              data-id="search-voice"
              data-search-keyword={search}
              data-current-voice-code={activeVoiceCode}
              data-voice-search-result={!!voices?.length}
              value={search}
              placeholder={t('search')}
              onChange={handleChangeSearch}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            {!isDubbingRoute && (
              <CommonButton
                onClick={handleRedirectToVoiceCloning}
                variant="containedSmall"
                color="primary"
                customStyle={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  borderRadius: '8px',
                  border: 'none',
                  height: '40px',
                }}
                className="create-voice-button"
              >
                <AddRounded />
                {t('createVoice')}
              </CommonButton>
            )}
          </Box>
        </DialogActions>
        <DialogContent className="content">
          <div className="web">
            <div className={classNames('voice-search', { active: openSort })}>
              <VoicesSort />
            </div>
            <div className={classNames('voice-search', { active: openFilter })}>
              <VoicesFilter
                filter={filterVoice}
                filterOpens={filterOpens}
                hasLanguageFilter={hasLanguageFilter}
                onChangeFilter={handleChangeFilter}
                onChangeFilterOpens={handleChangeFilterOpens}
                onHandleReset={handleReset}
              />
            </div>
          </div>
          <div className="voice-content">
            <ProcessHandler loading={loading} mt="20px" ml="20px" size={50}>
              <div className="voice-list">
                {voices.map((voiceItem) => (
                  <VoiceItem
                    voice={voiceItem}
                    activeVoiceId={activeVoiceId}
                    key={voiceItem.id}
                    usingPackageType={usingPackageType}
                    onChangePlayAudio={handleChangePlayAudio}
                    onSelectedVoice={handleSelectedVoice}
                    audioRef={audioRef}
                    setAudioType={setAudioType}
                  />
                ))}
                {voices?.length === 0 && (
                  <img
                    src={FirstVoiceBoxImg}
                    alt="first-voice-box"
                    className="empty-voices"
                  />
                )}
              </div>
            </ProcessHandler>
            <div className="pagination">
              {total > VOICE_PAGINATION_LIMIT && !loading && (
                <CustomPagination
                  count={Math.ceil(total / VOICE_PAGINATION_LIMIT)}
                  page={page}
                  onChangePage={handleChangePage}
                />
              )}
            </div>
          </div>
        </DialogContent>
      </StyledVoices>
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio ref={audioRef} onEnded={handleAudioEnd}>
        <source src={audioLink} type="audio/wav" />
      </audio>

      <MobileVoicesFilter
        open={openFilter}
        hasLanguageFilter={hasLanguageFilter}
        onClose={handleCloseVoiceFilter}
        filterVoice={filterVoice}
        filterOpens={filterOpens}
        onChangeFilter={handleChangeFilter}
        onChangeFilterOpens={handleChangeFilterOpens}
        onHandleReset={handleReset}
      />
    </Dialog>
  );
};

export default VoicesDialog;
